import axios from 'axios';

const AUTH_ERROR_STATUS = 401;
const AUTH_ERROR_MESSAGE_PATTERNS = ['invalid token', 'jwt expired', 'token expired', 'unauthorized'];
const OPAQUE_ERROR_MESSAGE_PATTERNS = ['failed to fetch', 'network error', 'networkerror', 'load failed'];
const REFRESH_EXPIRED_ERROR_CODE = 'NotAuthorizedException';
const REFRESH_EXPIRED_MESSAGE_PATTERNS = ['refresh token has expired', 'invalid refresh token'];

const getErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return undefined;
  }
  const { code } = error;
  return typeof code === 'string' ? code : undefined;
};

// Cognito surfaces refresh failures as plain objects rather than Error instances, so the message
// has to be read structurally as well.
const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error !== 'object' || error === null || !('message' in error)) {
    return undefined;
  }
  const { message } = error;
  return typeof message === 'string' ? message : undefined;
};

/**
 * Resolves the HTTP status carried by an error across both request layers, without relying on a
 * specific error class:
 * - axios errors expose it on `error.response.status`
 * - Cube.js `RequestError` exposes it on `error.status`
 */
export const getHttpErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  if ('response' in error) {
    const { response } = error;
    if (typeof response === 'object' && response !== null && 'status' in response) {
      const { status } = response;
      if (typeof status === 'number') {
        return status;
      }
    }
  }

  if ('status' in error) {
    const { status } = error;
    if (typeof status === 'number') {
      return status;
    }
  }

  return undefined;
};

/**
 * Detects an expired/invalid-token error. A message-based fallback covers 401s surfaced without a
 * numeric status.
 */
export const isAuthError = (error: unknown): boolean => {
  if (getHttpErrorStatus(error) === AUTH_ERROR_STATUS) {
    return true;
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return AUTH_ERROR_MESSAGE_PATTERNS.some(pattern => message.includes(pattern));
  }

  return false;
};

/**
 * Detects a Cognito refresh that failed because the refresh token is no longer usable — the
 * ordinary end of a session (the pool caps refresh tokens at one day), not a broken one.
 *
 * Cognito reports it as `NotAuthorizedException`, which also covers revoked tokens and disabled
 * users; every one of those calls for signing in again rather than requesting access, so matching
 * the code is the intended behaviour. The predicate is deliberately narrow: an unrecognised failure
 * means the stored session may be corrupt, and that must keep falling through to the recovery page.
 */
export const isRefreshTokenExpiredError = (error: unknown): boolean => {
  if (getErrorCode(error) === REFRESH_EXPIRED_ERROR_CODE) {
    return true;
  }

  const message = getErrorMessage(error)?.toLowerCase();
  if (message === undefined) {
    return false;
  }

  return REFRESH_EXPIRED_MESSAGE_PATTERNS.some(pattern => message.includes(pattern));
};

/**
 * Detects a request that failed without a readable HTTP status — a network error or, crucially,
 * a CORS-masked API-Gateway rejection (`net::ERR_FAILED`). These cannot be classified as auth by
 * status, but an expired/invalid token surfaces this way when the error response has no CORS
 * headers, so they are treated as a trigger for a single controlled refresh (only after retries
 * are exhausted, so a one-off blip does not escalate).
 *
 * Recognition is message-pattern based (`Failed to fetch` / `Load failed` / network errors) so
 * that unrelated runtime `TypeError`s (e.g. `Cannot read properties of undefined`) are not
 * misclassified as opaque request failures — a genuine `fetch()` network failure is itself a
 * `TypeError` whose message matches these patterns.
 */
export const isOpaqueRequestError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    return !error.response;
  }
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return OPAQUE_ERROR_MESSAGE_PATTERNS.some(pattern => message.includes(pattern));
  }
  return false;
};
